import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { DeviceType, BANNER_DIMENSIONS } from './types';
import type { BannerService } from './FileProcessingUtils';

interface UploadImageButtonProps {
  index: number;
  deviceType: DeviceType;
  // Optional callbacks and services
  banner?: BannerService;
  isUploading?: boolean;
  setIsUploading?: React.Dispatch<React.SetStateAction<boolean>>;
  setError?: React.Dispatch<React.SetStateAction<string | null>>;
  onImageUpload?: (index: number, deviceType: DeviceType, file: File, url: string) => void;
  setUploadedFiles?: React.Dispatch<React.SetStateAction<any[]>>;
}

const UploadImageButton: React.FC<UploadImageButtonProps> = ({
  index,
  deviceType,
  banner,
  isUploading,
  setIsUploading,
  setError,
  onImageUpload,
  setUploadedFiles,
}) => {
  // Default handlers (these will be overridden by props if provided)
  const defaultHandlers = {
    isUploading: isUploading || false,
    setIsUploading: setIsUploading || (() => {}),
    setError: setError || (() => {}),
    banner: banner || { uploadFile: async () => null, removeUploadingFile: () => {}, uploadingFiles: {} },
    setUploadedFiles: setUploadedFiles || (() => {}),
  };

  const onDeviceDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) {
        return;
      }

      const uploadFile = acceptedFiles[0];
      if (!uploadFile) {
        return;
      }

      defaultHandlers.setIsUploading(true);

      try {
        const s3Url = await defaultHandlers.banner.uploadFile(uploadFile);

        if (s3Url && onImageUpload) {
          // Use the callback if provided
          onImageUpload(index, deviceType, uploadFile, s3Url);
        } else if (s3Url && setUploadedFiles) {
          // Fallback to direct state update if no callback but we have setUploadedFiles
          setUploadedFiles((prev) => {
            const updated = [...prev];
            const file = updated[index];
            if (!file) {
              return prev;
            }

            const fileCopy = { ...file };

            // Ensure imageFiles is initialized
            if (!fileCopy.imageFiles) {
              fileCopy.imageFiles = {};
            }

            if (deviceType === DeviceType.DESKTOP) {
              fileCopy.imageUrl = s3Url;
              fileCopy.imageFiles[DeviceType.DESKTOP] = uploadFile;
            } else if (deviceType === DeviceType.TABLET) {
              fileCopy.tabletImageUrl = s3Url;
              fileCopy.imageFiles[DeviceType.TABLET] = uploadFile;
            } else if (deviceType === DeviceType.MOBILE) {
              fileCopy.mobileImageUrl = s3Url;
              fileCopy.imageFiles[DeviceType.MOBILE] = uploadFile;
            }

            updated[index] = fileCopy;
            return updated;
          });
        }
      } catch (err) {
        defaultHandlers.setError(err instanceof Error ? err.message : 'Failed to upload image');
      } finally {
        defaultHandlers.setIsUploading(false);
      }
    },
    [index, deviceType, onImageUpload, defaultHandlers, setUploadedFiles],
  );

  const {
    getRootProps: getDeviceRootProps,
    getInputProps: getDeviceInputProps,
    isDragActive: isDeviceDragActive,
  } = useDropzone({
    onDrop: onDeviceDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    },
    multiple: false,
  });

  const deviceLabels = {
    [DeviceType.DESKTOP]: {
      text: 'Desktop',
      size: BANNER_DIMENSIONS.DESKTOP.replace('x', '×'),
    },
    [DeviceType.TABLET]: {
      text: 'Tablet',
      size: BANNER_DIMENSIONS.TABLET.replace('x', '×'),
    },
    [DeviceType.MOBILE]: {
      text: 'Mobile',
      size: BANNER_DIMENSIONS.MOBILE.replace('x', '×'),
    },
  };

  const label = deviceLabels[deviceType];

  return (
    <div
      {...getDeviceRootProps()}
      className={`border border-2 border-secondary rounded p-3 text-center ${isDeviceDragActive ? 'bg-light' : 'bg-secondary bg-opacity-10'}`}
      style={{ cursor: 'pointer', height: '180px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
    >
      <input {...getDeviceInputProps()} />
      {isDeviceDragActive ? (
        <div>
          <h6 className="mb-2 fw-bold text-primary">Drop image here...</h6>
          <small className="text-muted d-block">Recommended size: {label.size}</small>
        </div>
      ) : (
        <div>
          <h6 className="mb-2 fw-bold text-primary">Add {label.text} Image</h6>
          <small className="text-muted d-block">Drag and drop or click to select</small>
          <small className="text-muted d-block mt-1">Recommended size: {label.size}</small>
        </div>
      )}
    </div>
  );
};

export default UploadImageButton;
